import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Settings, Pencil, PencilOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSiteContent } from '../context/SiteContentContext';
import AccountSettingsModal from './AccountSettingsModal';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { editMode, toggleEditMode, exitEditMode } = useSiteContent();

  const isLoggedIn = user && user !== false;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Repairs', path: '/repairs' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  // Auto-exit edit mode when user logs out
  React.useEffect(() => {
    if (!isLoggedIn && editMode) exitEditMode();
  }, [isLoggedIn, editMode, exitEditMode]);

  return (
    <>
      {/* Edit Mode Banner */}
      {editMode && isLoggedIn && (
        <div className="sticky top-0 z-[55] bg-[#0A84FF] text-white text-sm py-2 px-6 flex items-center justify-center gap-3" data-testid="edit-mode-banner">
          <Pencil className="w-4 h-4" strokeWidth={2} />
          <span className="font-medium">Edit Mode is ON</span>
          <span className="text-white/80">— hover any text with a blue dashed outline to edit it</span>
          <button
            onClick={exitEditMode}
            data-testid="exit-edit-mode-banner-btn"
            className="ml-2 px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 text-xs font-medium transition-colors"
          >
            Exit Edit Mode
          </button>
        </div>
      )}

      <header className={`sticky ${editMode && isLoggedIn ? 'top-[40px]' : 'top-0'} z-50 bg-[#0A0A0A]/70 backdrop-blur-xl border-b border-white/10`}>
        <nav className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
              <img
                src="/logo-mark.png?v=3"
                alt="Mac Guru logo"
                width="32"
                height="32"
                className="w-8 h-8 object-contain transition-all duration-300 group-hover:rotate-12"
              />
              <span className="text-xl font-semibold tracking-tight">Mac Guru</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isActive(link.path) ? 'text-[#0A84FF]' : 'text-[#A1A1AA] hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              {isLoggedIn && (
                <Link
                  to="/admin"
                  data-testid="nav-admin"
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isActive('/admin') ? 'text-[#0A84FF]' : 'text-[#A1A1AA] hover:text-white'
                  }`}
                >
                  Dashboard
                </Link>
              )}
            </div>

            <div className="hidden md:flex items-center gap-2">
              {isLoggedIn ? (
                <>
                  <button
                    onClick={toggleEditMode}
                    data-testid="toggle-edit-mode-btn"
                    title={editMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
                    className={`px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                      editMode
                        ? 'bg-[#FFD60A]/20 text-[#FFD60A] hover:bg-[#FFD60A]/30'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {editMode ? <PencilOff className="w-4 h-4" strokeWidth={1.5} /> : <Pencil className="w-4 h-4" strokeWidth={1.5} />}
                    {editMode ? 'Exit Edit' : 'Edit Text'}
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    data-testid="account-settings-btn"
                    title="Account Settings"
                    className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
                  >
                    <Settings className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={logout}
                    data-testid="logout-btn"
                    className="px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-white hover:bg-white/20 backdrop-blur-md transition-all duration-300"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  data-testid="login-btn"
                  className="px-3 py-2 rounded-full text-sm font-medium text-[#A1A1AA] hover:text-white transition-all duration-300"
                >
                  Log in
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-btn"
              className="md:hidden text-white"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-white/10 pt-4" data-testid="mobile-menu">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link key={link.path} to={link.path} onClick={() => setIsMenuOpen(false)}
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isActive(link.path) ? 'text-[#0A84FF]' : 'text-[#A1A1AA] hover:text-white'
                    }`}>
                    {link.name}
                  </Link>
                ))}
                {isLoggedIn && (
                  <Link to="/admin" onClick={() => setIsMenuOpen(false)}
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isActive('/admin') ? 'text-[#0A84FF]' : 'text-[#A1A1AA] hover:text-white'
                    }`}>
                    Dashboard
                  </Link>
                )}
                {isLoggedIn ? (
                  <>
                    <button onClick={() => { toggleEditMode(); setIsMenuOpen(false); }}
                      className="px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-white text-left">
                      {editMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
                    </button>
                    <button onClick={() => { setShowSettings(true); setIsMenuOpen(false); }}
                      className="px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-white text-left">
                      Account Settings
                    </button>
                    <button onClick={() => { logout(); setIsMenuOpen(false); }}
                      className="px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-white text-left">
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}
                    className="px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-white text-center">
                    Log in
                  </Link>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      <AccountSettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
    </>
  );
};
